import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { WorkflowApiService, WorkflowIntentApprovalTask, WorkflowIntentTimelineEvent } from './workflow-api.service';

@Component({
  standalone: true,
  selector: 'ain-workflow-intent-approval-inbox',
  imports: [
    AinPageComponent,
    AinStateComponent,
    FormsModule,
    RouterLink,
    TableModule,
    ButtonModule,
    TagModule,
    SelectModule,
    InputTextModule,
    CheckboxModule,
    TextareaModule,
    DialogModule,
    DatePipe
  ],
  template: `
    <ain-page [title]="t('workflow.intents.approvals.title')" [description]="t('workflow.intents.approvals.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="error() ?? ''" />
      } @else {
        <div class="filters">
          <div class="field">
            <label>{{ t('workflow.intents.approvals.filters.status') }}</label>
            <p-select
              [options]="statusOptions"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="statusFilter"
              (onChange)="reload()"></p-select>
          </div>
          <div class="field">
            <label>{{ t('workflow.intents.approvals.filters.intentKey') }}</label>
            <input pInputText [(ngModel)]="intentKeyFilter" (keydown.enter)="reload()" />
          </div>
          <div class="field checkbox">
            <p-checkbox binary="true" [(ngModel)]="mineOnly" (onChange)="reload()"></p-checkbox>
            <span>{{ t('workflow.intents.approvals.filters.mineOnly') }}</span>
          </div>
          <div class="field checkbox">
            <p-checkbox binary="true" [(ngModel)]="overdueOnly" (onChange)="reload()"></p-checkbox>
            <span>{{ t('workflow.intents.approvals.filters.overdueOnly') }}</span>
          </div>
          <button pButton type="button" [label]="t('common.search')" (click)="reload()"></button>
        </div>

        @if (items().length === 0) {
          <ain-state type="empty" [title]="t('workflow.intents.approvals.states.empty')" />
        } @else {
        <p-table [value]="items()">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('workflow.intents.approvals.columns.intentRequestId') }}</th>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('workflow.intents.approvals.columns.dueAt') }}</th>
              <th>{{ t('workflow.intents.approvals.columns.sla') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td><a [routerLink]="['/workflow/intents', row.intentRequestId]">{{ row.intentRequestId }}</a></td>
              <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
              <td [class.overdue]="isOverdue(row)">{{ row.dueAtUtc ? (row.dueAtUtc | date: 'short') : '-' }}</td>
              <td>{{ resolveSla(row) }}</td>
              <td>
                <div class="actions">
                  <button pButton type="button" text [label]="t('workflow.claim')" (click)="claim(row)"></button>
                  @if (row.status === 'Claimed') {
                    <button pButton type="button" text severity="secondary" [label]="t('workflow.intents.approvals.actions.unclaim')" (click)="unclaim(row)"></button>
                  }
                  <button pButton type="button" text severity="secondary" [label]="t('workflow.intents.approvals.actions.open')" (click)="openTimeline(row)"></button>
                  <button pButton type="button" text severity="success" [label]="t('workflow.intents.actions.approve')" (click)="openDecision(row, 'approve')"></button>
                  <button pButton type="button" text severity="danger" [label]="t('workflow.intents.actions.reject')" (click)="openDecision(row, 'reject')"></button>
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>
        }

        @if (selectedIntentId()) {
          <p-table [value]="selectedTimeline()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('columns.date') }}</th>
                <th>{{ t('workflow.intents.timeline.columns.action') }}</th>
                <th>{{ t('workflow.intents.timeline.columns.outcome') }}</th>
                <th>{{ t('workflow.intents.timeline.columns.actor') }}</th>
                <th>{{ t('columns.code') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td>{{ item.occurredAtUtc | date: 'short' }}</td>
                <td>{{ timelineActionLabel(item.eventType) }}</td>
                <td>{{ timelineOutcomeLabel(item) }}</td>
                <td>{{ timelineActorLabel(item.actorUserId) }}</td>
                <td>{{ item.code || '-' }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      }

      <p-dialog [(visible)]="decisionDialogVisible" [header]="dialogTitle()" [modal]="true" [style]="{ width: '32rem' }">
        <div class="dialog-body">
          <label>{{ t('workflow.intents.detail.fields.reason') }}</label>
          <textarea pTextarea rows="4" [(ngModel)]="decisionReason"></textarea>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="decisionDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="submitDecision()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .filters { display: grid; gap: 12px; margin-bottom: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); align-items: end; }
    .field { display: grid; gap: 6px; }
    .checkbox { display: flex; align-items: center; gap: 8px; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .overdue { color: #b91c1c; font-weight: 600; }
    .dialog-body { display: grid; gap: 8px; }
  `]
})
export class WorkflowIntentApprovalInboxComponent implements OnInit {
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly items = signal<WorkflowIntentApprovalTask[]>([]);
  readonly selectedIntentId = signal<string | null>(null);
  readonly selectedDecision = signal<'approve' | 'reject'>('approve');
  readonly selectedTimeline = signal<WorkflowIntentTimelineEvent[]>([]);

  statusFilter = '';
  intentKeyFilter = '';
  mineOnly = false;
  overdueOnly = false;
  decisionDialogVisible = false;
  decisionReason = '';

  get statusOptions(): Array<{ label: string; value: string }> {
    return [
      { label: this.t('common.all'), value: '' },
      { label: 'Pending', value: 'Pending' },
      { label: 'Claimed', value: 'Claimed' },
      { label: 'Expired', value: 'Expired' }
    ];
  }

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.queryIntentApprovalInbox({
      status: this.statusFilter || undefined,
      intentKey: this.intentKeyFilter || undefined,
      mineOnly: this.mineOnly ? true : undefined,
      overdueOnly: this.overdueOnly ? true : undefined,
      pageNumber: 1,
      pageSize: 50
    }).subscribe({
      next: response => {
        this.items.set(response.items ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  claim(item: WorkflowIntentApprovalTask): void {
    this.api.claimIntentApproval(item.intentRequestId, crypto.randomUUID()).subscribe({
      next: () => this.reload(),
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  unclaim(item: WorkflowIntentApprovalTask): void {
    this.api.unclaimIntentApproval(item.intentRequestId, crypto.randomUUID()).subscribe({
      next: () => this.reload(),
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  openDecision(item: WorkflowIntentApprovalTask, decision: 'approve' | 'reject'): void {
    this.selectedIntentId.set(item.intentRequestId);
    this.selectedDecision.set(decision);
    this.decisionReason = '';
    this.loadTimeline(item.intentRequestId);
    this.decisionDialogVisible = true;
  }

  openTimeline(item: WorkflowIntentApprovalTask): void {
    this.selectedIntentId.set(item.intentRequestId);
    this.loadTimeline(item.intentRequestId);
  }

  submitDecision(): void {
    const intentRequestId = this.selectedIntentId();
    if (!intentRequestId) {
      return;
    }

    this.api.runIntentOpsAction(intentRequestId, {
      action: this.selectedDecision(),
      idempotencyKey: crypto.randomUUID(),
      reason: this.decisionReason || undefined
    }).subscribe({
      next: () => {
        this.decisionDialogVisible = false;
        this.loadTimeline(intentRequestId);
        this.reload();
      },
      error: () => {
        this.decisionDialogVisible = false;
        this.error.set(this.t('common.saveFailed'));
      }
    });
  }

  statusSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' | 'secondary' {
    if (status === 'Pending') {
      return 'warn';
    }

    if (status === 'Claimed') {
      return 'info';
    }

    if (status === 'Expired') {
      return 'danger';
    }

    return 'secondary';
  }

  statusLabel(status: string): string {
    const key = `workflow.intents.timeline.status.${status.toLowerCase()}`;
    const localized = this.t(key);
    return localized === key ? status : localized;
  }

  timelineActionLabel(eventType: string): string {
    const key = `workflow.intents.timeline.events.${eventType.toLowerCase()}`;
    const localized = this.t(key);
    return localized === key ? eventType : localized;
  }

  timelineOutcomeLabel(item: WorkflowIntentTimelineEvent): string {
    if (item.code) {
      return item.code;
    }

    if (item.approvalStatus && item.approvalStatus !== 'None') {
      return this.statusLabel(item.approvalStatus);
    }

    return this.statusLabel(item.executionStatus);
  }

  timelineActorLabel(actorUserId?: string): string {
    if (!actorUserId) {
      return this.t('workflow.intents.timeline.actor.system');
    }

    return actorUserId;
  }

  isOverdue(item: WorkflowIntentApprovalTask): boolean {
    return !!item.dueAtUtc && new Date(item.dueAtUtc).getTime() < Date.now() && (item.status === 'Pending' || item.status === 'Claimed');
  }

  resolveSla(item: WorkflowIntentApprovalTask): string {
    if (item.slaMinutes === undefined || item.slaMinutes === null) {
      return '-';
    }

    return `${item.slaMinutes}m`;
  }

  dialogTitle(): string {
    return this.selectedDecision() === 'approve'
      ? this.t('workflow.intents.actions.approve')
      : this.t('workflow.intents.actions.reject');
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private loadTimeline(intentRequestId: string): void {
    this.api.getIntentApprovalTimeline(intentRequestId).subscribe({
      next: timeline => this.selectedTimeline.set(timeline),
      error: () => this.error.set(this.t('common.loadFailed'))
    });
  }
}
